import { useDesign } from "./store";
import { useLibrary } from "./library";
import {
  applySharedRoomUpdates,
  fetchSharedRoom,
  subscribeSharedRoom,
} from "./sharedRoomsCloud";
import {
  cloudToDesign,
  diffDesignToCloudUpdates,
  type SharedRoomDoc,
} from "./sharedRoomTypes";
import type { Design } from "./types";

/**
 * Two-way sync between {@link useDesign} and a `sharedRooms/{roomId}`
 * Firestore document, modeled after {@link import("./librarySync")}.
 *
 * Lifecycle:
 *   - Caller invokes {@link startSharedRoomSync}(roomId) when the user
 *     opens a shared room (typically via `?shared=<id>` URL param or
 *     "Shared with me" tab).
 *   - We fetch the doc, hydrate `useDesign` via `loadSharedDesign`, then
 *     attach two subscriptions:
 *
 *       1. Inbound: Firestore `onSnapshot` → reconcile into local state.
 *          Items the local user is currently dragging are skipped so the
 *          drag doesn't visually jitter while remote frames arrive.
 *
 *       2. Outbound: `useDesign.subscribe` → diff against the last-known
 *          cloud state and write only the changed dotted-path fields. The
 *          `applyingRemote` flag suppresses echoes from #1.
 *
 *   - {@link stopSharedRoomSync} tears everything down (e.g. on
 *     navigation away from the shared room).
 *
 * The orchestrator is intentionally **not** auto-started from auth state
 * (unlike `librarySync`): a shared room is opened explicitly via URL or
 * inbox click, never as a background sync.
 */

interface ActiveSession {
  roomId: string;
  /** Last-known cloud `Design` shape. Used as the base for outbound diffs. */
  cloudDesign: Design;
  /** Set while applying a remote snapshot so the local subscriber doesn't
   *  echo those writes back to Firestore. */
  applyingRemote: boolean;
  /** A pending write whose result will be observed via `onSnapshot`.
   *  When set, an arriving snapshot whose `updatedAt` is older than this
   *  is treated as our own echo and skipped. */
  lastLocalWriteAt: number;
  unsubSnapshot: () => void;
  unsubStore: () => void;
  /** Listeners notified of access loss / errors. */
  onAccessLost: ((reason: string) => void) | null;
}

let session: ActiveSession | null = null;

/**
 * Cached view of `session.cloudDesign` for React. Replaced only when the
 * persisted baseline actually changes — `useSyncExternalStore` loops if
 * `getSnapshot` returns a fresh object every call. Not updated on the
 * optimistic write inside `pushLocalDiff`; that lands after the write
 * succeeds, so an in-flight (or failed) push still counts as unsaved.
 */
interface SharedRoomBaseline {
  roomId: string;
  design: Design;
}

let baselineSnapshot: SharedRoomBaseline | null = null;
const baselineListeners = new Set<() => void>();

function publishSharedBaseline(): void {
  const next: SharedRoomBaseline | null = session
    ? { roomId: session.roomId, design: session.cloudDesign }
    : null;
  if (
    baselineSnapshot === next ||
    (baselineSnapshot !== null &&
      next !== null &&
      baselineSnapshot.roomId === next.roomId &&
      baselineSnapshot.design === next.design)
  ) {
    return;
  }
  baselineSnapshot = next;
  for (const listener of baselineListeners) listener();
}

export function subscribeSharedRoomBaseline(onStoreChange: () => void): () => void {
  baselineListeners.add(onStoreChange);
  return () => {
    baselineListeners.delete(onStoreChange);
  };
}

export function getSharedRoomBaseline(): SharedRoomBaseline | null {
  return baselineSnapshot;
}

/** Test-only. Installs the baseline `useHasUnsavedChanges` reads. */
export function _setSharedRoomBaselineForTests(
  next: SharedRoomBaseline | null
): void {
  baselineSnapshot = next;
  for (const listener of baselineListeners) listener();
}

export interface StartSharedRoomSyncOptions {
  /** Called when the room becomes inaccessible (deleted, kicked, etc.). */
  onAccessLost?: (reason: string) => void;
}

/**
 * Open the shared room, hydrate `useDesign`, and start the sync session.
 * Returns the `SharedRoomDoc` on success.
 *
 * Throws if the room is missing or the caller doesn't have read access.
 */
export async function startSharedRoomSync(
  roomId: string,
  opts: StartSharedRoomSyncOptions = {}
): Promise<SharedRoomDoc> {
  // Tear down any prior session — switching rooms must not leave the
  // store subscriber pointing at the old `lastLocalWriteAt`.
  stopSharedRoomSync();

  const doc = await fetchSharedRoom(roomId);
  if (!doc) {
    throw new Error("This shared room doesn't exist or you don't have access.");
  }

  const initialDesign = cloudToDesign(doc);

  // Hydrate the local store. This sets `activeSharedRoomId`, which is the
  // signal the outbound subscriber waits for before pushing diffs.
  useDesign.getState().loadSharedDesign(initialDesign, roomId);
  // The library card for this id still shows the last private snapshot.
  // Opening the shared room is how that snapshot catches up when the
  // edit happened on another device.
  useLibrary.getState().syncSavedDesign(roomId, initialDesign);

  const newSession: ActiveSession = {
    roomId,
    cloudDesign: structuredClone(initialDesign),
    applyingRemote: false,
    lastLocalWriteAt: 0,
    unsubSnapshot: () => {},
    unsubStore: () => {},
    onAccessLost: opts.onAccessLost ?? null,
  };
  session = newSession;
  publishSharedBaseline();

  newSession.unsubSnapshot = subscribeSharedRoom(
    roomId,
    (next) => {
      if (!session || session.roomId !== roomId) return;
      if (next === null) {
        session.onAccessLost?.(
          "The owner deleted this shared room or removed your access."
        );
        return;
      }
      reconcileFromRemote(next);
    },
    (err) => {
      if (!session || session.roomId !== roomId) return;
      // Most likely: the caller's membership was revoked. Treat as
      // access-loss so the UI can navigate away cleanly.
      session.onAccessLost?.(
        err.message ||
          "Lost access to this shared room. The owner may have removed you."
      );
    }
  );

  newSession.unsubStore = useDesign.subscribe((state, prev) => {
    if (!session || session.roomId !== roomId) return;
    if (state.activeSharedRoomId !== roomId) return;
    if (session.applyingRemote) return;
    // Trigger an outbound diff on either:
    //  1. A design mutation (the common case — add/remove/edit), OR
    //  2. A drag-flag clear (drag end). During a drag, position writes
    //     for the dragged item are suppressed in `diffDesignToCloudUpdates`
    //     so RTDB owns the live ghost. The final position only flows to
    //     Firestore once the flag flips back to null — and at that
    //     moment `design` hasn't changed, so we'd miss it without this
    //     explicit gate.
    const designChanged = state.design !== prev.design;
    const dragJustEnded =
      (prev.draggingFurnitureId !== null && state.draggingFurnitureId === null) ||
      (prev.draggingMountId !== null && state.draggingMountId === null) ||
      (prev.draggingOpeningId !== null && state.draggingOpeningId === null);
    if (!designChanged && !dragJustEnded) return;
    void pushLocalDiff();
  });

  return doc;
}

/** Tear down the active sync session, if any. */
export function stopSharedRoomSync(): void {
  if (!session) return;
  session.unsubSnapshot();
  session.unsubStore();
  session = null;
  publishSharedBaseline();
}

export function getActiveSharedRoomId(): string | null {
  return session?.roomId ?? null;
}

// ---------------------------------------------------------------------------
// Inbound: remote → local
// ---------------------------------------------------------------------------

function reconcileFromRemote(doc: SharedRoomDoc): void {
  if (!session) return;
  const incomingDesign = cloudToDesign(doc);

  const state = useDesign.getState();
  // Items the local user is mid-dragging are visually authoritative on
  // this device — the remote position from Firestore is stale until our
  // drag-end commit lands. Carry over our local copy of those items.
  const carriedDesign = mergeWithLocalDrags(incomingDesign, state.design, {
    draggingFurnitureId: state.draggingFurnitureId,
    draggingMountId: state.draggingMountId,
    draggingOpeningId: state.draggingOpeningId,
  });

  // Update the cloud-side cache (without the local-drag carry-over) so
  // outbound diffs are computed against the actual cloud state.
  session.cloudDesign = incomingDesign;

  const roomId = session.roomId;
  applyRemote(() => {
    useDesign.getState().applyRemoteDesign(carriedDesign);
  });
  publishSharedBaseline();

  // Thumbnail follows the persisted remote design, not the in-progress
  // drag carry-over. A mid-drag position isn't saved until drag end.
  useLibrary.getState().syncSavedDesign(roomId, incomingDesign);
}

interface DragSelection {
  draggingFurnitureId: string | null;
  draggingMountId: string | null;
  draggingOpeningId: string | null;
}

function mergeWithLocalDrags(
  incoming: Design,
  local: Design,
  drags: DragSelection
): Design {
  const out: Design = { ...incoming };

  if (drags.draggingFurnitureId) {
    const localItem = local.furniture.find(
      (f) => f.id === drags.draggingFurnitureId
    );
    if (localItem) {
      out.furniture = incoming.furniture.map((f) =>
        f.id === drags.draggingFurnitureId
          ? { ...f, x: localItem.x, y: localItem.y, rotation: localItem.rotation }
          : f
      );
      // If the dragging item was added locally and isn't in incoming yet,
      // append it so the drag preview doesn't disappear mid-drag.
      if (!out.furniture.some((f) => f.id === drags.draggingFurnitureId)) {
        out.furniture = [...out.furniture, localItem];
      }
    }
  }

  if (drags.draggingMountId) {
    const localItem = local.mounts.find(
      (m) => m.id === drags.draggingMountId
    );
    if (localItem) {
      out.mounts = incoming.mounts.map((m) =>
        m.id === drags.draggingMountId
          ? {
              ...m,
              wallIndex: localItem.wallIndex,
              alongWall: localItem.alongWall,
              fromFloor: localItem.fromFloor,
            }
          : m
      );
      if (!out.mounts.some((m) => m.id === drags.draggingMountId)) {
        out.mounts = [...out.mounts, localItem];
      }
    }
  }

  if (drags.draggingOpeningId) {
    const localItem = local.openings.find(
      (o) => o.id === drags.draggingOpeningId
    );
    if (localItem) {
      out.openings = incoming.openings.map((o) =>
        o.id === drags.draggingOpeningId
          ? {
              ...o,
              wallIndex: localItem.wallIndex,
              alongWall: localItem.alongWall,
              fromFloor: localItem.fromFloor,
            }
          : o
      );
      if (!out.openings.some((o) => o.id === drags.draggingOpeningId)) {
        out.openings = [...out.openings, localItem];
      }
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Outbound: local → remote
// ---------------------------------------------------------------------------

async function pushLocalDiff(): Promise<void> {
  if (!session) return;
  const state = useDesign.getState();
  if (state.activeSharedRoomId !== session.roomId) return;

  const updates = diffDesignToCloudUpdates(session.cloudDesign, state.design, {
    selfDraggingId: state.draggingFurnitureId,
    selfDraggingMountId: state.draggingMountId,
    selfDraggingOpeningId: state.draggingOpeningId,
  });
  if (Object.keys(updates).length === 0) return;

  // Optimistically update the cache so a subsequent diff doesn't
  // re-emit the same write before the snapshot round-trips.
  const roomId = session.roomId;
  const persisted = structuredClone(state.design);
  session.cloudDesign = persisted;
  session.lastLocalWriteAt = Date.now();

  try {
    await applySharedRoomUpdates(roomId, updates);
  } catch (err) {
    // Most likely: Firestore rules denied the write because we lost
    // membership. Surface as access-loss so the UI can react.
    // Leave the library snapshot and the unsaved baseline alone — the
    // shared save didn't land. `cloudDesign` was already advanced so the
    // next diff doesn't retry; the badge keeps the previous baseline.
    if (session && session.roomId) {
      session.onAccessLost?.(
        (err as Error).message ||
          "We couldn't save your change to the shared room."
      );
    }
    return;
  }

  if (session?.roomId === roomId && session.cloudDesign === persisted) {
    publishSharedBaseline();
  }
  useLibrary.getState().syncSavedDesign(roomId, persisted);
}

function applyRemote(fn: () => void): void {
  if (session) session.applyingRemote = true;
  try {
    fn();
  } finally {
    if (session) session.applyingRemote = false;
  }
}

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

export function _resetSharedRoomSyncForTests(): void {
  stopSharedRoomSync();
  if (baselineSnapshot !== null) {
    baselineSnapshot = null;
    for (const listener of baselineListeners) listener();
  }
}
